/**
 * PreviewNavigationSidebar - Navigation sidebar for preview mode with progress indicators
 * Requirements: 7.1, 7.4
 *
 * This component:
 * - Displays lesson with progress indicator
 * - Shows progress based on block completion
 * - Uses editor store for progress tracking (ephemeral, not persisted)
 */

import React, { useMemo } from 'react';
import {
  buildSingleLessonNavigationItems,
  buildSingleLessonProgressMap,
  isLessonCompleteBlock,
} from '@course-authoring/shared-types';
import { CourseNavigation } from '@course-authoring/ui-blocks';
import { useTranslation } from '@course-authoring/i18n';
import { useEditorStore } from '../store/editorStore';

/**
 * PreviewNavigationSidebar component
 *
 * Displays the lesson with progress indicator in preview mode.
 * Requirements:
 * - 7.1: Display progress indicators in the navigation sidebar
 * - 7.4: Update current location indicator
 */
export const PreviewNavigationSidebar: React.FC = () => {
  const { t } = useTranslation();
  const lessonMeta = useEditorStore((state) => state.lessonMeta);
  const blocks = useEditorStore((state) => state.blocks);
  const blockOrder = useEditorStore((state) => state.blockOrder);
  const previewBlockCompletion = useEditorStore((state) => state.previewBlockCompletion);
  const previewEngaged = useEditorStore((state) => state.previewEngaged);
  const resolvedLessonId = lessonMeta.id || 'lesson';

  const lessonCompleteBlockId = useMemo(() => {
    const found = blockOrder.find((blockId) => {
      const block = blocks[blockId];
      return block ? isLessonCompleteBlock(block) : false;
    });
    return found ?? null;
  }, [blockOrder, blocks]);

  const lessonProgressMap = useMemo(() => {
    return buildSingleLessonProgressMap(
      resolvedLessonId,
      blockOrder,
      previewBlockCompletion,
      previewEngaged,
      lessonCompleteBlockId
    );
  }, [resolvedLessonId, blockOrder, previewBlockCompletion, previewEngaged, lessonCompleteBlockId]);

  const navigationLessons = useMemo(() => {
    return buildSingleLessonNavigationItems(resolvedLessonId, lessonMeta.title);
  }, [resolvedLessonId, lessonMeta.title]);

  const estimatedTimeLabel = useMemo(
    () => formatEstimatedTimeLabel(lessonMeta.estimatedTimeMinutes, t),
    [lessonMeta.estimatedTimeMinutes, t]
  );

  // Width is owned by the wrapping CollapsibleNavLayout (inline sidebar + drawer);
  // CourseNavigation fills its container at 100%.
  return (
    <CourseNavigation
      title={lessonMeta.title || t('lesson_title.untitled')}
      lessons={navigationLessons}
      currentLessonId={resolvedLessonId}
      lessonProgress={lessonProgressMap}
      onLessonSelect={() => {}}
      layout="statusCard"
      estimatedTimeLabel={estimatedTimeLabel}
      data-testid="preview-navigation-sidebar"
    />
  );
};

PreviewNavigationSidebar.displayName = 'PreviewNavigationSidebar';

function formatEstimatedTimeLabel(
  estimatedTimeMinutes: number | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): string | undefined {
  if (estimatedTimeMinutes === undefined) return undefined;
  const roundedMinutes = Math.max(1, Math.round(estimatedTimeMinutes));
  return t('player.estimated_time', { minutes: roundedMinutes });
}
